import {useConfig} from '@aboutyou/config';
import {GetRecommendationsResponse_Recommendation} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  useTrackFeature,
  useTrackFeatureInViewImpression,
} from '@aboutyou/tracking';
import {
  BasketSection,
  Paper,
  mediaMobileOnly,
  mediaTabletAndUp,
  tabletAndUp,
} from '@aboutyou/ui-components';
import React from 'react';
import styled, {css} from 'styled-components';
import {
  AddToBasketPayload,
  StreamProductTileDeprecated,
} from '../../../features/ProductStream/Tiles/StreamProductTileDeprecated';
import {RecommendationsLane} from '../../../features/Recommendations/deprecated/RecommendationsLane';

interface ComplementaryRecommendationProp
  extends GetRecommendationsResponse_Recommendation {
  onClickAddToBasket: (tile?: AddToBasketPayload) => void;
  smallerPaddings?: boolean;
  showNavigation?: boolean;
  isSmallFormat?: boolean;
}

const TRACK_FEATURE_LABEL = 'basket-complementary-reco';

export const ComplementaryRecommendationView: React.FC<
  ComplementaryRecommendationProp
> = ({
  onClickAddToBasket,
  type: recommendation,
  smallerPaddings,
  showNavigation,
  isSmallFormat,
}) => {
  const {t} = useTranslation();
  const {device} = useConfig();
  const trackFeature = useTrackFeature();
  const {ref} = useTrackFeatureInViewImpression({label: TRACK_FEATURE_LABEL});

  switch (recommendation?.$case) {
    case 'products':
      const {products, headline, subline, promotionTracking} =
        recommendation.products;

      if (products.products.length < 1) {
        return null;
      }

      return (
        <BasketSection>
          <StyledPaper $smallerPadding={smallerPaddings}>
            <StyledRecommendationsLane
              ref={ref as React.RefObject<HTMLDivElement | null>}
              key={`products-complementary-recommendation`}
              content={{
                headline,
                subline,
                isSmallFormat,
              }}
              streamPosition={0}
              promotionTracking={promotionTracking}
              testid={`products-complementary-recommendation-lane`}
              productTileConfig={{
                onClickAddCta: onClickAddToBasket
                  ? (tile?: AddToBasketPayload) => onClickAddToBasket(tile)
                  : undefined,
                addCtaVisibility: 'always',
                showLowestPriorPriceHint: true,
                addCtaTitle: t('Add'),
                gridColumns: device === 'desktop' ? 4 : 2,
                onClick: () => {
                  trackFeature({
                    eventType: 'click',
                    label: TRACK_FEATURE_LABEL,
                  });
                },
              }}
              ProductTileComponent={StreamProductTileDeprecated}
              products={{
                $case: 'list',
                list: {
                  deprecatedProducts: products.products,
                  itemsPreferences: products.itemPreferences,
                },
              }}
              constrained={true}
              laneLayoutConfig={{
                hasHeaderBottomGap: !smallerPaddings,
                hideSideButtons: !showNavigation,
                disableHeaderControl: true,
              }}
              forceCenterTitle
              addHeadlinesMarginBottom
            />
          </StyledPaper>
        </BasketSection>
      );
  }
  return null;
};

const StyledRecommendationsLane = styled(RecommendationsLane)`
  margin-bottom: 0;
  padding-bottom: 0;
`;

const StyledPaper = styled(Paper)<{$smallerPadding?: boolean}>`
  @media ${mediaTabletAndUp} {
    margin-bottom: 14px;
  }
  @media ${mediaMobileOnly} {
    border-radius: unset;
  }
  padding: 1rem;

  ${tabletAndUp(
    css`
      padding: 1.5rem;
    `,
  )}

  ${({$smallerPadding}) =>
    $smallerPadding !== undefined ? `padding-right: 0px;` : ''}
`;
